import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { useEffect } from 'react';

// Hero Section with CTA
const ServiceHero = () => (
  <section className="relative min-h-[500px] flex items-center" data-testid="service-hero">
    <div 
      className="absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/e0gkd1b5_iStock-2175972769.jpg')`
      }}
    />
    <div className="absolute inset-0 bg-[#1a2e44]/80" />
    <div className="relative max-w-6xl mx-auto px-6 lg:px-8 py-20">
      <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 uppercase">
        Exterior Asset Care
      </h1>
      <p className="text-xl text-slate-300 max-w-2xl mb-8">
        Regular cleaning and upkeep of the visible elements that shape your property's appearance—signage, lighting fixtures, gates, and other exterior assets.
      </p>
      <Link
        to="/contact?intent=assessment&service=exterior&source=service-exterior-asset-care"
        className="inline-flex items-center justify-center bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-bold py-4 px-8 uppercase tracking-wide transition-colors border border-white/20"
        data-testid="hero-cta-btn"
      >
        Request a Site Assessment
        <ArrowRight className="w-4 h-4 ml-2" />
      </Link>
    </div>
  </section>
);

// What's Included Section
const IncludesSection = () => {
  const services = [
    'Gate and fence cleaning',
    'Exterior lighting fixture cleaning',
    'Garage door cleaning',
    'Property signage and address marker cleaning',
    'Mailbox and entry feature detailing',
    'Pool tile and cover cleaning',
    'Exterior fixture detailing'
  ];

  return (
    <section className="bg-white py-20" data-testid="includes-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-[#1a2e44] mb-4">
          What Our Care Programs Include
        </h2>
        <p className="text-slate-600 mb-10 max-w-2xl">
          Scheduled cleaning services for all exterior property elements:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {services.map((service, index) => (
            <div key={index} className="flex items-start gap-3 p-4 bg-slate-50">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">{service}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why It Matters Section
const WhyItMattersSection = () => (
  <section className="bg-[#1a2e44] py-20" data-testid="why-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-white mb-6">
        Why Exterior Asset Care Matters
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="bg-white/10 p-6">
          <h3 className="text-lg font-bold text-white mb-3">Complete Property Appearance</h3>
          <p className="text-slate-300 text-sm">
            Ensures every visible element of your property exterior is clean and well-maintained.
          </p>
        </div>
        <div className="bg-white/10 p-6">
          <h3 className="text-lg font-bold text-white mb-3">Consistent Standards</h3>
          <p className="text-slate-300 text-sm">
            Regular cleaning keeps gates, signage, and fixtures looking their best at all times.
          </p>
        </div>
        <div className="bg-white/10 p-6">
          <h3 className="text-lg font-bold text-white mb-3">First Impressions</h3>
          <p className="text-slate-300 text-sm">
            Clean exterior assets create a polished, welcoming appearance for visitors and residents.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// How We Deliver Section
const DeliverySection = () => (
  <section className="bg-slate-50 py-20" data-testid="delivery-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-[#1a2e44] mb-10">
        Our Structured Service Process
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">01</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Asset Inventory</h3>
          <p className="text-slate-600 text-sm">
            We catalog all exterior assets and assess their condition.
          </p>
        </div>
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">02</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Care Schedule</h3>
          <p className="text-slate-600 text-sm">
            We develop an inspection schedule for each asset type.
          </p>
        </div>
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">03</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Routine Inspections</h3>
          <p className="text-slate-600 text-sm">
            We perform scheduled checks and handle routine care.
          </p>
        </div>
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">04</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Documentation</h3>
          <p className="text-slate-600 text-sm">
            Every inspection is documented with findings and photos.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Documentation Section
const DocumentationSection = () => (
  <section className="bg-white py-20" data-testid="documentation-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-3xl font-bold text-[#1a2e44] mb-6">
            Documentation & Reporting
          </h2>
          <p className="text-slate-600 mb-8">
            Our exterior asset care includes thorough documentation:
          </p>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Asset condition reports with photos</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Care activity logs</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Issue identification and recommendations</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Coordination notes for specialty repairs</span>
            </li>
          </ul>
        </div>
        <div className="bg-slate-50 p-8 border border-slate-200">
          <p className="text-slate-600 italic text-lg">
            "Our documentation keeps you informed about the condition of every exterior property element."
          </p>
        </div>
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="bg-[#1a2e44] py-20" data-testid="cta-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-3xl font-bold text-white mb-4">
        Schedule a Property Assessment
      </h2>
      <p className="text-slate-400 mb-8 max-w-xl mx-auto">
        Contact us to assess your property's exterior assets and develop a comprehensive care program.
      </p>
      <Link
        to="/contact?intent=assessment&service=exterior&source=service-exterior-asset-care"
        className="inline-flex items-center justify-center bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-bold py-4 px-8 uppercase tracking-wide transition-colors border border-white/20"
        data-testid="cta-btn"
      >
        Request a Site Assessment
        <ArrowRight className="w-4 h-4 ml-2" />
      </Link>
    </div>
  </section>
);

// Service Area Section
const ServiceAreaSection = () => (
  <section className="bg-slate-50 py-16" data-testid="service-area-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-2xl font-bold text-[#1a2e44] mb-4">
        Service Area
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">
        Serving Pasadena, Arcadia, San Marino, La Cañada Flintridge, and communities throughout Los Angeles County.
      </p>
    </div>
  </section>
);

export default function ExteriorAssetCarePage() {
  useEffect(() => {
    document.title = 'Exterior Asset Care | Sunlight Grounds Care';
  }, []);

  return (
    <div data-testid="exterior-asset-care-page">
      <ServiceHero />
      <IncludesSection />
      <WhyItMattersSection />
      <DeliverySection />
      <DocumentationSection />
      <CTASection />
      <ServiceAreaSection />
    </div>
  );
}
